import { useMemo, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Badge } from '../ui'
import { LabFlow } from './LabFlow'
import {
  DemoHeader,
  InsightCard,
  OntologyResponsePanel,
  UserActionPanel,
} from './GoalDrivenDemo'
import { getContractPath, getIncidentPath, getRfqPath } from '../../data/labPaths'
import { useT } from '../../lib/i18n'
import type { OntoPathNode } from '../../types'
import type { LabDemoProps } from './types'

const PATH_BY_DOC: Record<string, () => OntoPathNode[]> = {
  contract: getContractPath,
  rfq: getRfqPath,
  incident: getIncidentPath,
}

export function DocumentDrivenDemo({ paradigm, lab, data }: LabDemoProps) {
  const t = useT()
  const demo = (data as typeof t.labDemos.documentDriven | undefined) ?? t.labDemos.documentDriven
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null)

  const selectedDoc = useMemo(
    () => (selectedDocId ? demo.documents.find((d) => d.id === selectedDocId) ?? null : null),
    [selectedDocId, demo.documents],
  )

  const path = useMemo<OntoPathNode[]>(() => {
    if (!selectedDoc) return []
    const gen = PATH_BY_DOC[selectedDoc.id]
    return gen ? gen() : []
  }, [selectedDoc])

  return (
    <div className="space-y-5">
      <DemoHeader paradigm={paradigm} />

      <div className="flex flex-col gap-4">
        <UserActionPanel
          label={lab.userActionLabel}
          title={demo.documentsLabel}
          hint={demo.documentsHint}
        >
          <div className="grid sm:grid-cols-3 gap-2">
            {demo.documents.map((doc) => {
              const active = doc.id === selectedDocId
              return (
                <button
                  key={doc.id}
                  type="button"
                  onClick={() => setSelectedDocId(doc.id)}
                  aria-pressed={active}
                  className={`text-left p-3.5 rounded-[var(--radius-md)] transition-colors ${
                    active
                      ? 'bg-[color:var(--color-orion-amber)]/15 border border-[color:var(--color-orion-amber)]/60'
                      : 'border-faint hover:bg-[color:var(--color-orion-deep)]/40'
                  }`}
                >
                  <div className="flex items-baseline gap-2 mb-1.5">
                    <span className="text-[20px] leading-none">{doc.icon}</span>
                    <span
                      className={`mono text-[length:var(--text-small)] truncate ${
                        active ? 'text-[color:var(--color-orion-text)]' : 'text-[color:var(--color-orion-haze)]'
                      }`}
                    >
                      {doc.name}
                    </span>
                  </div>
                  <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-haze)] mb-1">
                    {doc.meta}
                  </div>
                  <p
                    className="text-[color:var(--color-orion-text)] leading-snug"
                    style={{ fontSize: 'var(--text-small)' }}
                  >
                    {doc.summary}
                  </p>
                </button>
              )
            })}
          </div>

          {selectedDoc && (
            <div className="mt-4 pt-4 border-t border-[color:var(--color-orion-surface)]/40">
              <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
                <span className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)]">
                  {demo.extractedLabel}
                </span>
                <button
                  type="button"
                  onClick={() => setSelectedDocId(null)}
                  className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-haze)] hover:text-[color:var(--color-orion-text)] transition-colors"
                >
                  {demo.resetButton}
                </button>
              </div>
              <AnimatePresence mode="wait">
                <motion.div
                  key={selectedDoc.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex flex-wrap gap-2"
                >
                  {selectedDoc.entities.map((ent, i) => (
                    <motion.div
                      key={`${selectedDoc.id}-${i}`}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.08 + i * 0.1, duration: 0.3 }}
                      className="flex items-baseline gap-2 px-2.5 py-1 rounded-[var(--radius-sm)] border-faint bg-[color:var(--color-orion-deep)]/40"
                    >
                      <span className="mono text-[10px] uppercase tracking-widest text-[color:var(--color-orion-haze)]">
                        {ent.kind}
                      </span>
                      <span
                        className="text-[color:var(--color-orion-text)]"
                        style={{ fontSize: 'var(--text-small)' }}
                      >
                        {ent.value}
                      </span>
                    </motion.div>
                  ))}
                </motion.div>
              </AnimatePresence>
            </div>
          )}
        </UserActionPanel>

        <OntologyResponsePanel label={lab.ontologyResponseLabel}>
          {selectedDoc ? (
            <LabFlow
              path={path}
              kindLabels={t.l3.kindLabels}
              pathKey={`document-driven::${selectedDoc.id}`}
              minHeight={360}
              maxHeight={520}
            />
          ) : (
            <EmptyState />
          )}
        </OntologyResponsePanel>
      </div>

      <InsightCard title={demo.insightTitle} body={demo.insightBody} />
    </div>
  )
}

function EmptyState() {
  return (
    <div className="min-h-[200px] flex items-center justify-center">
      <Badge tone="neutral">⤴ Pick a document above</Badge>
    </div>
  )
}
